/**
 * Story 057 — Add-turbine modal (`/turbines?new=1`).
 *
 * Required: Park (siteId) + Asset type (assetTypeId) + Asset model
 * (assetModelId) — AC15. Save disabled until all three are filled.
 *
 * Asset type field is ALWAYS rendered (Christian's Q6 lock 2026-05-13).
 * Asset model field is disabled until Asset type is picked; the
 * cascading fetch fires `listAssetModels?assetTypeId=<id>` on Type
 * change. Options are grouped "Frequently used" (`scope: 'tenant'`) /
 * "All models" (`scope: 'global'`) — Christian's Q5 lock.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../../account/ToastContext.js";
import {
  useAssetModelsForTypeQuery,
  useCreateTurbineMutation,
  type CreateTurbineBody,
  type TurbineExternalId,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import {
  SiteAutocomplete,
  type SiteOption,
} from "../shared/SiteAutocomplete.js";
import { AssetTypeAutocomplete } from "../shared/AssetTypeAutocomplete.js";
import { AssetModelAutocomplete } from "../shared/AssetModelAutocomplete.js";

/**
 * Wire-shape of the BE error envelope. The api-gateway writes 400s via
 * `validation-error.filter.ts` (any `{ code: string, ... }` body
 * passes through), 409s + 403s + 401s + 410s direct from the throw
 * site. Fields are individually optional; the dispatch logic in
 * `planForError` works off whichever ones are present.
 */
interface BackendErrorBody {
  code?: string;
  detail?: string;
  reason?: string;
  message?: string;
}

/**
 * Translate a thrown `assetsFetch` rejection into a UI plan: either an
 * inline error keyed to a form field, or a toast string, or both. The
 * mapping is centralised so future error codes can be added in one
 * place without re-threading through the catch block.
 *
 * R58-W-03 makes this surface root-cause-aware:
 *   - `400 forbidden_field: <key>` (BE-032 §invariant 1) → managed-field
 *     toast (FE bug — should be unreachable post-strip).
 *   - `400 model_type_mismatch` → inline error on the Model field
 *     (cascade verify failed — usually a stale dropdown).
 *   - `409 site_inactive` → friendly toast naming the deactivated site.
 *   - `403 forbidden` → RBAC-aware toast naming the missing permission.
 *   - everything else → the generic fallback (preserves pre-W-03 UX).
 */
interface ErrorPlan {
  /** When set, attached to a specific form field via react-hook-form. */
  inlineErrors?: Partial<
    Record<"siteId" | "assetTypeId" | "assetModelId", string>
  >;
  /** When set, fires `toast(toast.message, toast.severity)`. */
  toast?: { message: string; severity: "error" | "info" };
  /** When set, written to the in-form `submitError` panel for context. */
  formMessage?: string;
}

function planForError(err: unknown): ErrorPlan {
  const status = (err as { status?: number }).status;
  const body = (err as { body?: BackendErrorBody }).body ?? {};
  const code = (body.code ?? "").toLowerCase();
  const detail = (body.detail ?? "").toLowerCase();
  const reason = (body.reason ?? "").toLowerCase();

  /** Panel-friendly explanation string (or `null` if neither
   *  `detail` nor `message` was supplied). Centralised so the
   *  exactOptionalPropertyTypes optional-key dance lives in one place. */
  function panelText(): string | null {
    const t = body.detail ?? body.message ?? "";
    return t.length > 0 ? t : null;
  }
  function withFormMessage(plan: ErrorPlan): ErrorPlan {
    const t = panelText();
    return t === null ? plan : { ...plan, formMessage: t };
  }

  // 400 validation_error — distinguish forbidden_field, cascade
  // mismatch, and generic field-validation issues.
  if (status === 400 && code === "validation_error") {
    if (detail.startsWith("forbidden_field")) {
      // FE should have stripped these — the toast wording matches the
      // pre-W-03 path so Story 057 AC18 stays green.
      return withFormMessage({
        toast: {
          message: "Cannot set this field — it's managed by the system.",
          severity: "error",
        },
      });
    }
    if (detail === "model_type_mismatch") {
      return {
        inlineErrors: {
          assetModelId:
            "Model is not compatible with the selected asset type. Pick a different model.",
        },
      };
    }
    if (detail === "model_not_found") {
      return {
        inlineErrors: {
          assetModelId:
            "The selected model is no longer in the catalog. Pick a different model.",
        },
      };
    }
    // Generic class-validator failure — `message` is a string from
    // validation-error.filter's normalisation.
    return withFormMessage({
      toast: {
        message: body.message ?? "Some fields are invalid. Check and retry.",
        severity: "error",
      },
    });
  }

  // 409 conflict — currently only `site_inactive` ships from the BE.
  if (status === 409 && code === "conflict" && reason === "site_inactive") {
    return {
      toast: {
        message: "The selected site is deactivated. Choose another.",
        severity: "error",
      },
    };
  }

  // 403 forbidden — the create path only sees the role gate; the
  // narrower `site_id_mutation_requires_tenant_admin` reason is a
  // PATCH-only `updateTurbine` path.
  if (status === 403 && code === "forbidden") {
    return {
      toast: {
        message: "You don't have permission to add turbines.",
        severity: "error",
      },
    };
  }

  // 401 unauthorized — the umbrella shell catches this elsewhere
  // and bounces to /login, but cover the case for resilience.
  if (status === 401 && code === "unauthorized") {
    return {
      toast: {
        message: "Your session expired. Sign in again.",
        severity: "error",
      },
    };
  }

  // Fallback — preserves pre-W-03 UX so unfamiliar paths still surface
  // SOMETHING actionable.
  return withFormMessage({
    toast: { message: "Couldn't add turbine. Try again.", severity: "error" },
  });
}

interface AddTurbineFormValues {
  siteId: string | null;
  assetTypeId: string | null;
  assetModelId: string | null;
  internalParkNumber: string;
  identifier: string;
  latitude: string;
  longitude: string;
  hubHeight: string;
  rotorDiameter: string;
  totalHeight: string;
  yearBuilt: string;
  commissionedAt: string;
  externalIds: TurbineExternalId[];
}

const EMPTY: AddTurbineFormValues = {
  siteId: null,
  assetTypeId: null,
  assetModelId: null,
  internalParkNumber: "",
  identifier: "",
  latitude: "",
  longitude: "",
  hubHeight: "",
  rotorDiameter: "",
  totalHeight: "",
  yearBuilt: "",
  commissionedAt: "",
  externalIds: [],
};

function toNumberOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function toIntOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}
function dropEmptyExternalIds(arr: TurbineExternalId[]): TurbineExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildCreateBody(v: AddTurbineFormValues): CreateTurbineBody {
  // Required fields are validated by `canSave` before submit; cast
  // through `string` here.
  const body: CreateTurbineBody = {
    siteId: v.siteId ?? "",
    assetTypeId: v.assetTypeId ?? "",
    assetModelId: v.assetModelId ?? "",
  };
  if (v.internalParkNumber.trim().length > 0) {
    body.internalParkNumber = v.internalParkNumber.trim();
  }
  if (v.identifier.trim().length > 0) {
    body.identifier = v.identifier.trim();
  }
  const lat = toNumberOrNull(v.latitude);
  if (lat !== null) body.latitude = lat;
  const lon = toNumberOrNull(v.longitude);
  if (lon !== null) body.longitude = lon;
  const hub = toNumberOrNull(v.hubHeight);
  if (hub !== null) body.hubHeight = hub;
  const rotor = toNumberOrNull(v.rotorDiameter);
  if (rotor !== null) body.rotorDiameter = rotor;
  const total = toNumberOrNull(v.totalHeight);
  if (total !== null) body.totalHeight = total;
  const yr = toIntOrNull(v.yearBuilt);
  if (yr !== null) body.yearBuilt = yr;
  if (v.commissionedAt.trim().length > 0) {
    body.commissionedAt = v.commissionedAt.trim();
  }
  const ids = dropEmptyExternalIds(v.externalIds);
  if (ids.length > 0) body.externalIds = ids;
  return body;
}

export interface AddTurbineDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active sites (for the Park Autocomplete). */
  siteOptions: readonly SiteOption[];
}

export function AddTurbineDialog({
  open,
  onClose,
  siteOptions,
}: AddTurbineDialogProps): ReactElement {
  const { toast } = useToast();
  const create = useCreateTurbineMutation();
  // SB2-CC-04 — defence-in-depth banner for deep-link-bypass cases.
  const canCreate = useCanEditResourceFor("turbine.create");
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState,
  } = useForm<AddTurbineFormValues>({
    defaultValues: EMPTY,
    mode: "onChange",
  });

  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      reset(EMPTY);
      setSubmitError(null);
    }
  }, [open, reset]);

  const siteId = watch("siteId");
  const assetTypeId = watch("assetTypeId");
  const assetModelId = watch("assetModelId");

  // Spec 062 — fetch the asset_types rollup unconditionally (no
  // assetTypeId param) so the Asset type dropdown always has its
  // options. Then refetch with assetTypeId on Type change so the
  // Asset model dropdown narrows.
  const allModelsQuery = useAssetModelsForTypeQuery(null);
  const cascadingModelsQuery = useAssetModelsForTypeQuery(assetTypeId);

  const assetTypeOptions = allModelsQuery.data?.assetTypes ?? [];
  // Stabilise the array reference so the useEffect dep below doesn't
  // fire every render — empty `[]` allocations are not equal under
  // identity comparison.
  const cascadingModels = useMemo(
    () => cascadingModelsQuery.data?.items ?? [],
    [cascadingModelsQuery.data?.items],
  );

  // When Type changes, clear the Model picker if it no longer matches.
  useEffect(() => {
    if (assetTypeId === null) {
      if (assetModelId !== null) {
        setValue("assetModelId", null, { shouldDirty: true });
      }
      return;
    }
    if (assetModelId === null) return;
    const stillValid = cascadingModels.some(
      (m) => m.id === assetModelId && m.assetTypeId === assetTypeId,
    );
    if (!stillValid && cascadingModelsQuery.isFetched) {
      setValue("assetModelId", null, { shouldDirty: true });
    }
  }, [
    assetTypeId,
    assetModelId,
    cascadingModels,
    cascadingModelsQuery.isFetched,
    setValue,
  ]);

  const canSave =
    siteId !== null &&
    assetTypeId !== null &&
    assetModelId !== null &&
    !create.isPending;

  async function onSubmit(values: AddTurbineFormValues): Promise<void> {
    setSubmitError(null);
    // Clear any prior inline errors before retrying — without this a
    // user fixing a `model_type_mismatch` would see the red helperText
    // until the next mutation result.
    clearErrors(["siteId", "assetTypeId", "assetModelId"]);
    try {
      const created = await create.mutateAsync(buildCreateBody(values));
      toast(`Turbine added (code ${String(created.assetCode)}).`);
      onClose();
    } catch (err) {
      const plan = planForError(err);
      if (plan.inlineErrors) {
        for (const [field, message] of Object.entries(plan.inlineErrors)) {
          if (typeof message === "string" && message.length > 0) {
            setError(field as keyof AddTurbineFormValues, {
              type: "server",
              message,
            });
          }
        }
      }
      if (plan.toast) {
        toast(plan.toast.message, plan.toast.severity);
      }
      if (plan.formMessage) {
        setSubmitError(plan.formMessage);
      }
    }
  }

  // The Park dropdown sources from `siteOptions` directly. The caller
  // (TurbinesPage) already filters out deactivated sites via the
  // `listSites` projection — UX brief §4.2 row 1.
  const activeSiteOptions = siteOptions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-turbine-heading"
    >
      <DialogTitle id="add-turbine-heading">Add turbine</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          {!canCreate ? (
            <Alert
              severity="info"
              variant="outlined"
              role="status"
              aria-live="polite"
              data-testid="add-modal-permission-banner"
              sx={{ mb: 2 }}
            >
              Your role does not allow adding a turbine.
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <Controller
              name="siteId"
              control={control}
              render={({ field }) => (
                <SiteAutocomplete
                  value={field.value}
                  options={activeSiteOptions}
                  onChange={field.onChange}
                  required
                />
              )}
            />
            <Controller
              name="assetTypeId"
              control={control}
              render={({ field }) => (
                <AssetTypeAutocomplete
                  value={field.value}
                  options={assetTypeOptions}
                  loading={allModelsQuery.isPending}
                  onChange={(next) => {
                    field.onChange(next);
                    // Reset Model when Type changes (cascade trigger).
                    setValue("assetModelId", null, { shouldDirty: true });
                  }}
                  required
                />
              )}
            />
            <Controller
              name="assetModelId"
              control={control}
              render={({ field, fieldState }) => (
                <AssetModelAutocomplete
                  value={field.value}
                  options={cascadingModels}
                  loading={
                    assetTypeId !== null && cascadingModelsQuery.isPending
                  }
                  disabled={assetTypeId === null}
                  onChange={(next) => {
                    // Clear server-side cascade error as soon as the
                    // user picks a different model — the helperText
                    // should not stick while the form is otherwise
                    // valid again.
                    if (fieldState.error?.type === "server") {
                      clearErrors("assetModelId");
                    }
                    field.onChange(next);
                  }}
                  required
                  {...(fieldState.error?.message
                    ? { errorText: fieldState.error.message }
                    : {})}
                />
              )}
            />
            <TextField
              {...register("internalParkNumber")}
              label="Internal Park Name"
              helperText="Operator's tag within the site (e.g. WEA01). Leave empty to use the system code."
              slotProps={{
                htmlInput: {
                  "aria-label": "Internal Park Name",
                  maxLength: 50,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("identifier")}
              label="Identifier"
              helperText="Manufacturer serial number."
              slotProps={{
                htmlInput: {
                  "aria-label": "Identifier",
                  maxLength: 100,
                },
              }}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("latitude")}
                label="Latitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Latitude",
                    step: "any",
                    min: -90,
                    max: 90,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("longitude")}
                label="Longitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Longitude",
                    step: "any",
                    min: -180,
                    max: 180,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("hubHeight")}
                label="Hub height (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Hub height",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("rotorDiameter")}
                label="Rotor diameter (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Rotor diameter",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("totalHeight")}
                label="Total height (m)"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Total height",
                    step: "any",
                    min: 0,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("yearBuilt")}
                label="Year built"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Year built",
                    step: 1,
                    min: 1980,
                    max: new Date().getFullYear() + 5,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("commissionedAt")}
                label="Commissioned at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "Commissioned at" },
                }}
                fullWidth
              />
            </Stack>
            <ExternalIdsField control={control} register={register} />
            {submitError ? (
              <Box
                role="alert"
                sx={{
                  color: "error.main",
                  fontSize: 13,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,69,58,0.10)"
                      : "rgba(255,59,48,0.06)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                {submitError}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          {canCreate ? (
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave || formState.isSubmitting}
              startIcon={
                create.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
